(() => {
  if (window.bimoniHomeInitialized) return;
  window.bimoniHomeInitialized = true;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initHero(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const choices = [...root.querySelectorAll('.bm-hero-choice')];
    const slides = [...root.querySelectorAll('.bm-hero-panels > .bm-hero')];
    const dots = [...root.querySelectorAll('[data-hero-dot]')];
    const toggle = root.querySelector('[data-hero-toggle]');
    if (slides.length < 2 || !toggle) return;
    let current = Math.max(0, choices.findIndex(choice => choice.checked));
    let playing = !motion.matches;
    let timer = null;
    let visible = false;
    let hovering = false;
    let touchX = 0;
    let firstImageReady = false;
    let showRequest = 0;
    const imagePromises = new WeakMap();
    const prepareSlide = (slide, priority = 'low') => {
      const template = slide.querySelector('template[data-bm-hero-media]');
      if (template) template.replaceWith(template.content.cloneNode(true));
      const images = [...slide.querySelectorAll('img')];
      images.forEach(image => { image.loading = 'eager'; image.fetchPriority = priority; });
      if (!imagePromises.has(slide)) {
        imagePromises.set(slide, Promise.all(images.map(image => {
          if (image.complete) return Promise.resolve();
          return new Promise(resolve => {
            image.addEventListener('load', resolve, {once:true});
            image.addEventListener('error', resolve, {once:true});
          });
        })));
      }
      return imagePromises.get(slide);
    };
    const clear = () => { window.clearInterval(timer); timer = null; };
    const syncToggle = () => {
      toggle.setAttribute('aria-label', playing ? toggle.dataset.pause : toggle.dataset.play);
      toggle.setAttribute('aria-pressed', String(!playing));
      toggle.querySelector('[data-pause-icon]').toggleAttribute('hidden', !playing);
      toggle.querySelector('[data-play-icon]').toggleAttribute('hidden', playing);
    };
    const show = async (index, urgent = false) => {
      const target = (index + slides.length) % slides.length;
      const request = ++showRequest;
      await prepareSlide(slides[target], urgent || target === 0 ? 'high' : 'low');
      if (request !== showRequest) return;
      current = target;
      root.dataset.active = String(current);
      choices.forEach((choice, i) => { choice.checked = i === current; });
      slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', String(i !== current));
        slide.inert = i !== current;
      });
      dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === current)));
    };
    const start = () => {
      clear();
      if (!firstImageReady || !playing || !visible || hovering || document.hidden || (root.contains(document.activeElement) && document.activeElement !== toggle)) return;
      timer = window.setInterval(() => show(current + 1), 4000);
    };
    const manual = () => { playing = false; clear(); syncToggle(); };
    choices.forEach((choice, index) => choice.addEventListener('change', () => { manual(); show(index, true); }));
    dots.forEach((dot, index) => dot.addEventListener('click', () => { manual(); show(index, true); }));
    root.querySelectorAll('[data-hero-step]').forEach(button => button.addEventListener('click', () => {
      manual(); show(current + Number(button.dataset.heroStep), true);
    }));
    toggle.addEventListener('click', () => { playing = !playing; syncToggle(); start(); });
    root.addEventListener('pointerdown', event => { if (!event.target.closest('[data-hero-toggle]')) manual(); });
    root.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) { hovering = true; clear(); } });
    root.addEventListener('mouseleave', () => { hovering = false; start(); });
    root.addEventListener('focusin', clear);
    root.addEventListener('focusout', () => window.requestAnimationFrame(start));
    root.addEventListener('touchstart', event => { touchX = event.changedTouches[0].clientX; }, {passive:true});
    root.addEventListener('touchend', event => {
      const delta = event.changedTouches[0].clientX - touchX;
      if (Math.abs(delta) > 45) { manual(); show(current + (delta < 0 ? 1 : -1), true); }
    }, {passive:true});
    document.addEventListener('visibilitychange', start);
    motion.addEventListener('change', () => { if (motion.matches) manual(); });
    new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      start();
    }, {threshold:0.15}).observe(root);
    root.classList.add('is-enhanced');
    root.querySelector('.bm-hero-controls').hidden = false;
    show(current);
    syncToggle();
    prepareSlide(slides[current], 'high').then(() => {
      firstImageReady = true;
      start();
      if (navigator.connection && navigator.connection.saveData) return;
      const warmNext = () => prepareSlide(slides[(current + 1) % slides.length], 'low');
      if ('requestIdleCallback' in window) window.requestIdleCallback(warmNext, {timeout:1500});
      else window.setTimeout(warmNext, 250);
    });
  }

  function initCarousel(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const track = root.querySelector('.bm-product-grid');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;
    root.classList.add('is-carousel');
    root.querySelector('.bm-carousel-controls').hidden = false;
    const sync = () => {
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 3;
    };
    const move = direction => {
      const first = track.firstElementChild;
      if (!first) return;
      const step = first.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
      const count = Math.max(1, Math.floor((track.clientWidth + 1) / step));
      track.scrollBy({left:direction * step * count,behavior:motion.matches ? 'instant' : 'smooth'});
    };
    prev.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', sync, {passive:true});
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      }
    });
    new ResizeObserver(sync).observe(track);
    sync();
  }

  function initHoverImages(card) {
    if (card.dataset.bmHoverReady) return;
    card.dataset.bmHoverReady = 'true';
    const load = () => {
      const template = card.querySelector('template[data-bm-hover-media]');
      if (!template) return;
      const container = template.parentElement;
      template.replaceWith(template.content.cloneNode(true));
      const image = container.querySelector('img');
      if (!image) return;
      const ready = () => { if (image.naturalWidth) container.classList.add('bm-hover-ready'); };
      image.loading = 'eager';
      image.fetchPriority = 'low';
      image.addEventListener('load', ready, {once:true});
      if (image.complete) ready();
    };
    card.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'touch') load();
    });
    card.addEventListener('focusin', load, {once:true});
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-bm-hero]').forEach(initHero);
    scope.querySelectorAll('[data-bm-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-bm-hover-card]').forEach(initHoverImages);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
